import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
} from "react-native";
import { Ionicons, Feather } from "@expo/vector-icons";
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";

import Colors from "../../../Resources/Color";
import { useDevice } from "../../_layout";
import CompetitionFederationSearchRow from "./CompetitionFederationSearchRow";
import { getCompetitionFederationSuggestions } from "./competitionFederationConfig";
import { shouldShowCompetitionFederationEnterHint } from "./competitionFederationSearchMenuState";

const isEditableEventTarget = (target) => {
  if (!target || typeof window === "undefined") {
    return false;
  }

  const tagName = (target.tagName || "").toLowerCase();
  if (tagName === "input" || tagName === "textarea" || tagName === "select") {
    return true;
  }

  return target.isContentEditable === true;
};

const CompetitionFederationSearchMenu = ({
  value,
  onSelect,
  triggerLabel,
  searchPlaceholder,
  emptyLabel,
  suggestionsLabel,
  initialSuggestions = [],
  portalRoot = null,
}) => {
  const { isMobile } = useDevice();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isTriggerHovered, setIsTriggerHovered] = useState(false);
  const [isClearHovered, setIsClearHovered] = useState(false);
  const searchInputRef = useRef(null);
  const scrollViewRef = useRef(null);
  const keyboardNavigationRef = useRef(false);

  const hasQuery = searchQuery.trim().length > 0;
  const suggestions = useMemo(
    () =>
      hasQuery ? getCompetitionFederationSuggestions(searchQuery) : initialSuggestions,
    [hasQuery, initialSuggestions, searchQuery],
  );

  const handleOpenChange = useCallback((nextOpen, event, reason) => {
    const eventType =
      event && typeof event.type !== "undefined" ? event.type : null;
    const changeReason = typeof reason !== "undefined" ? reason : null;
    const shouldIgnoreFocusOutClose =
      nextOpen === false &&
      (changeReason === "focus-out" || eventType === "focusout");
    if (shouldIgnoreFocusOutClose) {
      return;
    }
    setIsOpen(nextOpen);
  }, []);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
    placement: "bottom-start",
    middleware: [
      offset(6),
      flip({ fallbackAxisSideDirection: "start" }),
      shift({ padding: 10 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "menu" });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    dismiss,
    role,
  ]);
  const stopTriggerEventPropagation = useCallback((event) => {
    event?.stopPropagation?.();
  }, []);
  const modalScopedPortalRoot =
    refs.reference.current?.closest?.(
      '[data-efortcoach-floating-root="true"]',
    ) ?? null;
  const resolvedPortalRoot =
    (portalRoot && portalRoot.isConnected === false ? null : portalRoot) ??
    (modalScopedPortalRoot && modalScopedPortalRoot.isConnected === false
      ? null
      : modalScopedPortalRoot) ??
    refs.reference.current?.ownerDocument?.body ??
    null;

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
    setSelectedIndex(0);
    keyboardNavigationRef.current = false;
  }, []);

  const toggleMenu = useCallback(() => {
    if (isOpen) {
      closeMenu();
      return;
    }

    setSearchQuery("");
    setSelectedIndex(0);
    keyboardNavigationRef.current = false;
    setIsOpen(true);
  }, [closeMenu, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSelectedIndex(0);
      return;
    }

    if (isMobile) {
      return;
    }

    let rafId = null;
    rafId = requestAnimationFrame(() => {
      searchInputRef.current?.focus?.();
    });

    return () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isMobile, isOpen]);

  useEffect(() => {
    if (selectedIndex < suggestions.length) {
      return;
    }

    setSelectedIndex(0);
  }, [selectedIndex, suggestions.length]);

  const selectFederation = useCallback(
    (federation) => {
      onSelect?.(federation);
      closeMenu();
    },
    [closeMenu, onSelect],
  );

  const handleKeyDown = useCallback(
    (event) => {
      const key = event?.key;
      if (!key) {
        return;
      }

      if (key === "ArrowDown") {
        event.preventDefault?.();
        keyboardNavigationRef.current = true;
        setSelectedIndex((prev) =>
          suggestions.length === 0 ? 0 : (prev + 1) % suggestions.length,
        );
        return;
      }

      if (key === "ArrowUp") {
        event.preventDefault?.();
        keyboardNavigationRef.current = true;
        setSelectedIndex((prev) =>
          suggestions.length === 0
            ? 0
            : (prev - 1 + suggestions.length) % suggestions.length,
        );
        return;
      }

      if (key === "Enter") {
        if (!suggestions[selectedIndex]) {
          return;
        }
        event.preventDefault?.();
        selectFederation(suggestions[selectedIndex]);
        return;
      }

      if (key === "Escape") {
        event.preventDefault?.();
        closeMenu();
      }
    },
    [closeMenu, selectFederation, selectedIndex, suggestions],
  );

  useEffect(() => {
    if (!isOpen || typeof window === "undefined") {
      return;
    }

    const onKeyDown = (event) => {
      const key = event?.key;
      if (
        key !== "ArrowDown" &&
        key !== "ArrowUp" &&
        key !== "Enter" &&
        key !== "Escape"
      ) {
        return;
      }

      const floatingElement = refs?.floating?.current;
      const isInsideFloating =
        !!floatingElement && typeof floatingElement.contains === "function"
          ? floatingElement.contains(event?.target)
          : false;

      if (!isInsideFloating && isEditableEventTarget(event?.target)) {
        return;
      }

      event?.preventDefault?.();
      event?.stopPropagation?.();
      handleKeyDown(event);
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [handleKeyDown, isOpen, refs]);

  const handleMouseMove = useCallback(() => {
    if (!isMobile) {
      keyboardNavigationRef.current = false;
    }
  }, [isMobile]);

  const triggerText = value || triggerLabel;

  return (
    <>
      <TouchableOpacity
        ref={refs.setReference}
        collapsable={false}
        onPressIn={stopTriggerEventPropagation}
        onMouseDown={stopTriggerEventPropagation}
        onPointerDown={stopTriggerEventPropagation}
        onTouchStart={stopTriggerEventPropagation}
        onPress={toggleMenu}
        activeOpacity={0.8}
        onMouseEnter={() => !isMobile && setIsTriggerHovered(true)}
        onMouseLeave={() => setIsTriggerHovered(false)}
        style={[
          styles.trigger,
          {
            borderColor: isTriggerHovered
              ? "rgba(0, 0, 0, 0.5)"
              : Colors.borderColor,
          },
        ]}
        {...getReferenceProps()}
      >
        <Text
          style={[
            styles.triggerText,
            { color: value ? Colors.blackColor : Colors.grayColor },
          ]}
          numberOfLines={1}
        >
          {triggerText}
        </Text>
        <Feather
          name={isOpen ? "chevron-up" : "chevron-down"}
          size={18}
          color={Colors.grayColor}
        />
      </TouchableOpacity>

      {isOpen && (
        <FloatingPortal root={resolvedPortalRoot ?? undefined}>
          <FloatingFocusManager context={context} modal={false}>
            <View
              ref={refs.setFloating}
              style={[styles.menuContainer, floatingStyles]}
              {...getFloatingProps({
                onMouseDown: stopTriggerEventPropagation,
                onClick: stopTriggerEventPropagation,
                onPointerDown: stopTriggerEventPropagation,
                onTouchStart: stopTriggerEventPropagation,
              })}
            >
              <View style={styles.searchRow}>
                <Ionicons
                  name="search-outline"
                  size={14}
                  color={Colors.grayColor}
                  style={{ marginRight: 8 }}
                />
                <TextInput
                  ref={searchInputRef}
                  value={searchQuery}
                  onChangeText={(text) => {
                    keyboardNavigationRef.current = false;
                    setSearchQuery(text);
                    setSelectedIndex(0);
                  }}
                  placeholder={searchPlaceholder}
                  placeholderTextColor={Colors.grayColor}
                  autoCorrect={false}
                  autoCapitalize="characters"
                  style={styles.searchInput}
                />
                {searchQuery.length > 0 && (
                  <TouchableOpacity
                    onPress={() => {
                      setSearchQuery("");
                      setSelectedIndex(0);
                      keyboardNavigationRef.current = false;
                      searchInputRef.current?.focus?.();
                    }}
                    onMouseEnter={() => !isMobile && setIsClearHovered(true)}
                    onMouseLeave={() => setIsClearHovered(false)}
                    style={{ padding: 2 }}
                  >
                    <Ionicons
                      name="close-circle-outline"
                      size={14}
                      color={
                        isClearHovered ? Colors.darkgrayColor : Colors.grayColor
                      }
                    />
                  </TouchableOpacity>
                )}
              </View>

              {suggestions.length > 0 ? (
                <ScrollView
                  ref={scrollViewRef}
                  style={{ maxHeight: 260 }}
                  contentContainerStyle={styles.resultsContent}
                  keyboardShouldPersistTaps="handled"
                  onMouseMove={handleMouseMove}
                >
                  {!hasQuery && !!suggestionsLabel && (
                    <View style={styles.suggestionsHeader}>
                      <Text style={styles.suggestionsHeaderText}>
                        {suggestionsLabel}
                      </Text>
                    </View>
                  )}
                  {suggestions.map((federation, index) => {
                    const showEnterHint = shouldShowCompetitionFederationEnterHint(
                      {
                        selectedIndex,
                        rowIndex: index,
                        suggestionsLength: suggestions.length,
                      }
                    );
                    return (
                      <CompetitionFederationSearchRow
                        key={federation}
                        federation={federation}
                        isSelected={selectedIndex === index}
                        isCurrentValue={value === federation}
                        showEnterHint={showEnterHint}
                        onSelect={() => selectFederation(federation)}
                        onHover={() => {
                          if (!isMobile && !keyboardNavigationRef.current) {
                            setSelectedIndex(index);
                          }
                        }}
                        index={index}
                        totalResults={suggestions.length}
                        scrollContainerRef={scrollViewRef}
                        keyboardNavigationRef={keyboardNavigationRef}
                      />
                    );
                  })}
                </ScrollView>
              ) : (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyStateText}>{emptyLabel}</Text>
                </View>
              )}
            </View>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  trigger: {
    width: "100%",
    height: 34,
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: Colors.whiteColor,
  },
  triggerText: {
    fontSize: 13,
    flex: 1,
    marginRight: 8,
  },
  menuContainer: {
    width: 240,
    backgroundColor: Colors.whiteColor,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
    boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
    overflow: "hidden",
    zIndex: 99999,
  },
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    height: 42,
    borderBottomWidth: 0.5,
    borderBottomColor: Colors.borderColor,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    color: Colors.blackColor,
    outlineStyle: "none",
    paddingVertical: 0,
  },
  resultsContent: {
    paddingVertical: 4,
    paddingHorizontal: 0,
  },
  suggestionsHeader: {
    paddingHorizontal: 14,
    marginHorizontal: 0,
    marginTop: 4,
    paddingBottom: 8,
  },
  suggestionsHeaderText: {
    fontSize: 11,
    fontWeight: "600",
    color: Colors.grayColor,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  emptyState: {
    paddingVertical: 20,
    paddingHorizontal: 18,
  },
  emptyStateText: {
    fontSize: 12,
    color: Colors.grayColor,
    textAlign: "center",
  },
});

export default CompetitionFederationSearchMenu;
